/**
 * Helpers for aggregating the signed-in user's LMSR positions on the market detail page.
 */

export function aggregateUserSharesByOption(
  rows: Array<{ market_option_id?: number | null; shares_owned?: number | null }>,
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const row of rows) {
    const oid = row.market_option_id;
    if (oid == null || typeof oid !== 'number') continue;
    const sh = Number(row.shares_owned ?? 0);
    if (!Number.isFinite(sh) || sh <= 0) continue;
    out[oid] = (out[oid] ?? 0) + sh;
  }
  return out;
}

/**
 * Weighted average entry probability per option (fair_price_at_trade is stored as 0–1).
 * Returns implied probability in 0–100 for chart reference lines.
 */
export function computeAvgBuyPercentByOptionForLmsr(
  rows: Array<{
    market_option_id?: number | null;
    shares_owned?: number | null;
    fair_price_at_trade?: number | null;
  }>,
): Record<number, number> {
  type Agg = { weight: number; sumFp: number };
  const byOpt = new Map<number, Agg>();

  for (const row of rows) {
    const oid = row.market_option_id;
    if (oid == null || typeof oid !== 'number') continue;
    const sh = Number(row.shares_owned ?? 0);
    if (!Number.isFinite(sh) || sh <= 0) continue;
    const fp = row.fair_price_at_trade;
    if (fp == null) continue;
    const fpn = Number(fp);
    if (!Number.isFinite(fpn)) continue;

    const cur = byOpt.get(oid) ?? { weight: 0, sumFp: 0 };
    cur.weight += sh;
    cur.sumFp += fpn * sh;
    byOpt.set(oid, cur);
  }

  const out: Record<number, number> = {};
  for (const [oid, { weight, sumFp }] of byOpt) {
    if (weight <= 0) continue;
    out[oid] = (sumFp / weight) * 100;
  }
  return out;
}
